import { useMutation, useQueryClient } from '@tanstack/react-query';
import { reservations, parseApiError } from '@ooh/contracts';
import type {
  BatchReserveRequest,
  BatchReserveResponse,
  BatchReleaseRequest,
  BatchReleaseResponse,
  BatchReleaseSelectedResponse,
  BatchReservePreviewRequest,
  BatchReservePreviewResponse,
  BatchReleasePreviewRequest,
  BatchReleasePreviewResponse,
} from '@ooh/contracts';
import { toast } from '@ooh/ui/components/sonner';
import { invalidateReservationCaches } from './mutation-helpers';
import { useApiClient } from '../../plugs/api-client';
import { apiErrorToUserMessage } from '../../lib/api-error-messages';

// ─── Batch reserve (unified SELECT popover — Rezerwacja / Opcja) ──────

/**
 * Issues one `POST /reservations/batch-reserve` call for every free cell in a
 * multi-select selection. Per-cell conflicts are reported in the structured
 * `conflicted` response array, not thrown — the promise only rejects on a
 * whole-request rejection (e.g. schema cap exceeded, permission denied).
 */
export function useBatchReserve() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<BatchReserveResponse, Error, BatchReserveRequest>({
    mutationFn: (vars) => api.call(reservations.batchReserve, vars),
    onSuccess: () => invalidateReservationCaches(queryClient, undefined),
    onError: (error) => toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation')),
  });
}

// ─── Batch release (unified SELECT popover — Zwolnij) ─────────────────

/**
 * Issues one `POST /reservations/batch-release` call for every own releasable
 * cell in a multi-select selection. Per-reservation skips (already SOLD, not
 * held, not found) are reported in the structured `skipped` response array.
 */
export function useBatchRelease() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<BatchReleaseResponse, Error, BatchReleaseRequest>({
    mutationFn: (vars) => api.call(reservations.batchRelease, vars),
    onSuccess: () => invalidateReservationCaches(queryClient, undefined),
    onError: (error) => toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation')),
  });
}

export interface SelectedRangeReleaseParams {
  reservationId: number;
  mediaUnitId: number;
  /** ISO date-only strings (YYYY-MM-DD). */
  dateFrom: string;
  dateTo: string;
}

/**
 * Shared `mode='selected'` batch-release call for a single reservation/date-range —
 * the same request shape `InspectQuickActions`' Anuluj/Wymuś zwolnienie and
 * `CancelDialog`'s "release only part" choice both issue (CU-86cbh03pj INSPECT/
 * reservation-panel parity). Narrows the response to `BatchReleaseSelectedResponse`
 * (guaranteed by `mode: 'selected'`) before invoking `onSuccess`.
 */
export function useSelectedRangeRelease() {
  const batchRelease = useBatchRelease();

  function releaseSelectedRange(
    params: SelectedRangeReleaseParams,
    onSuccess: (response: BatchReleaseSelectedResponse) => void,
  ) {
    batchRelease.mutate(
      {
        reservationIds: [params.reservationId],
        cells: [
          {
            mediaUnitId: params.mediaUnitId,
            dateFrom: new Date(params.dateFrom),
            dateTo: new Date(params.dateTo),
          },
        ],
        mode: 'selected',
      },
      {
        onSuccess: (response) => {
          if ('outcomes' in response) onSuccess(response);
        },
      },
    );
  }

  return { releaseSelectedRange, isPending: batchRelease.isPending };
}

// ─── Batch reserve TTL preview (reserve-confirmation dialog) ──────────

/**
 * Read-only preview of the automatic `ttlExpiresAt` each cell would receive
 * from a batch-reserve call — never writes a reservation row. Used by
 * `UnifiedSelectPopover`'s reserve-confirmation dialog to prefill/blank its
 * date picker before the caller commits (CU-86cak92z8). A failure here must
 * not block confirming with the automatic (server-computed) TTL, so the
 * global `MutationCache` fallback toast is suppressed — the dialog itself
 * degrades gracefully to a blank, unprefilled date field on error.
 */
export function useBatchReservePreviewTtl() {
  const api = useApiClient();

  return useMutation<BatchReservePreviewResponse, Error, BatchReservePreviewRequest>({
    mutationFn: (vars) => api.call(reservations.batchReservePreview, vars),
    onError: () => undefined,
  });
}

// ─── Batch release preview (Zwolnij confirmation breakdown) ───────────

/**
 * Read-only preview of what a batch-release call would do to the caller's
 * selected nośnik-and-window pairs — resolves server-side which pairs the
 * caller holds (would be released) and which the caller only has a
 * waiting-queue entry on (would be left), per reservation-engine's "Batch
 * release preview" (action-breakdown-remaining-dialogs task 4.4). Fired
 * eagerly on dialog-open, same convention as `useBatchReservePreviewTtl`.
 * Unlike that TTL preview, a failure here blocks the release (the confirm
 * control waits on it) rather than degrading gracefully, so the global
 * `MutationCache` toast is suppressed — the confirmation dialog's own
 * breakdown-error state already tells the caller and offers a retry.
 */
export function useBatchReleasePreview() {
  const api = useApiClient();

  return useMutation<BatchReleasePreviewResponse, Error, BatchReleasePreviewRequest>({
    mutationFn: (vars) => api.call(reservations.batchReleasePreview, vars),
    onError: () => undefined,
  });
}
